import { useCallback, useRef, useState } from 'react';

export type MutationStatus = 'idle' | 'submitting' | 'success' | 'error';

export interface MutationState<Value, Input> {
  readonly data: Value | null;
  readonly error: unknown | null;
  readonly mutate: (input: Input) => Promise<Value | undefined>;
  readonly reset: () => void;
  readonly status: MutationStatus;
}

export function useMutation<Input, Value>(
  mutation: (input: Input) => Promise<Value>,
): MutationState<Value, Input> {
  const submitting = useRef(false);
  const [status, setStatus] = useState<MutationStatus>('idle');
  const [data, setData] = useState<Value | null>(null);
  const [error, setError] = useState<unknown | null>(null);

  const mutate = useCallback(
    async (input: Input): Promise<Value | undefined> => {
      if (submitting.current) return undefined;

      submitting.current = true;
      setStatus('submitting');
      setData(null);
      setError(null);
      try {
        const result = await mutation(input);
        setData(result);
        setStatus('success');
        return result;
      } catch (mutationError) {
        setError(mutationError);
        setStatus('error');
        throw mutationError;
      } finally {
        submitting.current = false;
      }
    },
    [mutation],
  );

  const reset = useCallback(() => {
    if (submitting.current) return;
    setData(null);
    setError(null);
    setStatus('idle');
  }, []);

  return { data, error, mutate, reset, status };
}
